import React from 'react';
import { ITableDataSource } from 'models/table';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { ERouteLinks } from 'models/route';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from 'hooks/useTranslations';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { formatDate } from 'utils/date';
import { EPermission } from 'models/permissions';
import { Chips } from 'components/atoms/Chips';
import { usePermission } from 'hooks/usePermission';
import {
  getDictionaryValue,
  getLicenceStatusChipVariant,
} from 'pages/LegalEntitiesPage/LegalEntityViewLicencePage/helpers';
import { IDictionary } from 'models/dictionary';
import { StyledLegalLink } from 'components/atoms/StyledLegalLink';
import { getLegalEntityType } from 'components/organisms/RegisteredFirearmDetails';
import { ELegalEntityTypesLabel, LETypes } from 'constants/LegalEntityTypes';

interface IFirearmLicenceTableCard {
  data: ITableDataSource;
  dateFormat: string;
  weaponLicenceStatus: IDictionary[] | null;
}

const FirearmLicenceTableCard = ({
  data,
  dateFormat,
  weaponLicenceStatus,
}: IFirearmLicenceTableCard) => {
  const navigate = useNavigate();
  const { t } = useTranslations();
  const { hasPermissions } = usePermission();

  return (
    <Grid
      container
      sx={{
        border: `1px solid ${colors.gray200}`,
        borderRadius: '4px',
        overflow: 'hidden',
        mt: 1,
        backgroundColor: colors.gray100,
      }}
    >
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('registeredFirearms.firearmsDetails.licences.accordion.licenceNumber.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.licenceNumber)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('registeredFirearms.firearmsDetails.licences.accordion.licenceName.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.licenceName)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('registeredFirearms.firearmsDetails.licences.accordion.issueDate.column')}
        </Typography>
        <Typography variant="body2">
          {emptyFieldHolder(formatDate(data.issueDate, dateFormat))}
        </Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('registeredFirearms.firearmsDetails.licences.accordion.startDate.column')}
        </Typography>
        <Typography variant="body2">
          {emptyFieldHolder(formatDate(data.startDate, dateFormat))}
        </Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('registeredFirearms.firearmsDetails.licences.accordion.endDate.column')}
        </Typography>
        <Typography variant="body2">
          {emptyFieldHolder(formatDate(data.endDate, dateFormat))}
        </Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('registeredFirearms.firearmsDetails.licences.accordion.status.column')}
        </Typography>
        <Chips
          text={getDictionaryValue(data.statusUniqueId, weaponLicenceStatus || [])}
          variant={getLicenceStatusChipVariant(data.statusUniqueId)}
          fullWidth
        />
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('registeredFirearms.firearmsDetails.licences.accordion.owner.column')}
        </Typography>
        <StyledLegalLink
          legalId={data.ownerUniqueId}
          legalType={getLegalEntityType(data.ownerLegalTypeUniqueId)}
          labelText={data.owner}
          legalVisibilityUniqueId={data.ownerVisibilityUniqueId}
        />
      </Grid>

      <Grid
        container
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderTop: {
            xs: `1px solid ${colors.primary100}`,
            lg: 'none',
          },
          m: {
            xs: 2,
            lg: 2,
          },
        }}
        spacing={1}
      >
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography variant="button" sx={{ textTransform: 'uppercase' }}>
            {`${t('actions.general.label')}: `}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <Button
            label={t('registeredFirearms.firearmsDetails.licences.accordion.view.link')}
            variant={EButtonVariants.outlined}
            permission={EPermission.DISCARD_TRANSACTION}
            onClick={() =>
              navigate(
                `${ERouteLinks.DetailLicence.replace(':licenceId', data.id as string)}?type=${data.legalType === LETypes.individual
                  ? ELegalEntityTypesLabel.individual
                  : ELegalEntityTypesLabel.organisation
                }`,
              )
            }
            size="small"
            fullWidth
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export { FirearmLicenceTableCard };
